"use client";

import { useEffect } from "react";

/**
 * In-house affiliate visitor tracker. Mounted once in the root layout.
 *
 * Behavior:
 * 1. On every page load, read `?ref=CODE` from URL.
 * 2. If present, generate/reuse a stable `visitor_id` (random uuid), store
 *    both in a 60-day cookie (`lateral_aff_ref=CODE.VISITOR_ID`).
 * 3. Fire a background POST to /api/affiliate/track logging the click
 *    server-side (IP, referrer, landing). Non-blocking, never breaks page.
 * 4. If ?ref param absent, do nothing. Existing cookie is preserved.
 *
 * At checkout time, <getAffiliateRef()> reads the cookie and the Stripe
 * Checkout Session carries it as `client_reference_id` so the webhook can
 * create a commission record.
 *
 * Security: visitor_id is client-generated (not sensitive), affiliate code
 * is public by design. Cookie SameSite=Lax so it survives Stripe redirect
 * back to our domain. No PII written client-side.
 */
const COOKIE_NAME = "lateral_aff_ref";
const COOKIE_MAX_AGE_SEC = 60 * 24 * 60 * 60; // 60 days

export function AffiliateTracker() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (!ref) return;

    // Validate code format — alphanumeric + hyphen/underscore, max 32 chars.
    // Rejects garbage / injection attempts before we ever send to the API.
    if (!/^[a-zA-Z0-9_-]{2,32}$/.test(ref)) return;

    // Reuse existing visitor_id if cookie already has one (stable across
    // clicks to different affiliate links — last click wins for code).
    const existing = readCookie(COOKIE_NAME);
    const [existingRef, existingVid] = existing?.split(".") ?? [];
    const visitorId = existingVid || generateId();

    // Always update cookie with latest ref code (last-click attribution).
    setCookie(COOKIE_NAME, `${ref}.${visitorId}`, COOKIE_MAX_AGE_SEC);

    // Only log a click server-side if ref changed or visitor is new.
    if (existingRef !== ref) {
      fetch("/api/affiliate/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ref,
          visitorId,
          landing: window.location.pathname,
          referrer: document.referrer || null,
        }),
        // keepalive so the request survives navigation on Safari
        keepalive: true,
      }).catch(() => { /* silent — tracking must never break UX */ });
    }

    // Clean ?ref= from the visible URL so the user cannot accidentally share
    // someone else's affiliate code (or their own) when copy-pasting the URL.
    // Cookie already captured the code, so stripping the query param is safe.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("ref");
      const clean = url.pathname + (url.search || "") + url.hash;
      window.history.replaceState(null, "", clean);
    } catch {
      /* non-fatal */
    }
  }, []);

  return null;
}

/** Read the current affiliate ref from the cookie. Null if not tracking. */
export function getAffiliateRef(): string | null {
  if (typeof window === "undefined") return null;
  const v = readCookie(COOKIE_NAME);
  if (!v) return null;
  const [ref] = v.split(".");
  return ref || null;
}

/** Low-level cookie helpers — intentionally minimal, no 3rd party. */
function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, maxAgeSec: number) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSec}; Path=/; SameSite=Lax${secure}`;
}

function generateId(): string {
  // Browser crypto preferred; fallback to Math.random + timestamp (still unique enough for attribution).
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `vid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
